import { Category, getStaticCategory } from '@/api/models';
import { EntityDrawerView } from '@/components/EntityDrawerView';
import { CategoryFormData } from '@/pages/categories/hooks/useCategoryMutations';
import * as React from 'react';
import { CategoryForm } from './CategoryForm';

export interface CategoryDrawerViewProps {
  isOpen: boolean;
  category: Category | null;
  mode: 'view' | 'create' | 'edit';
  isLoading: boolean;
  isFormLoading: boolean;
  onClose: () => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onFormSubmit: (data: CategoryFormData) => Promise<void>;
}

export const CategoryDrawerView: React.FC<CategoryDrawerViewProps> = ({
  isOpen,
  category,
  mode,
  isLoading,
  isFormLoading,
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
}) => (
  <EntityDrawerView<Category, CategoryFormData>
    isOpen={isOpen}
    entity={category}
    mode={mode}
    isLoading={isLoading}
    isFormLoading={isFormLoading}
    entityLabel='Category'
    spinnerColor='border-coffice-yellow'
    onClose={onClose}
    onEdit={onEdit}
    onDelete={onDelete}
    onFormSubmit={onFormSubmit}
    getTitle={(entity, m) => {
      const staticCategory = entity ? getStaticCategory(entity.name) : null;
      switch (m) {
        case 'create':
          return 'Create New Category';
        case 'edit':
          return entity
            ? `Edit ${staticCategory?.label || entity.name}`
            : 'Edit Category';
        default:
          return entity
            ? staticCategory?.label || entity.name
            : 'Category Details';
      }
    }}
    renderHeader={entity => {
      const staticCategory = getStaticCategory(entity.name);
      return (
        <div className='flex items-center space-x-4 pb-6 border-b border-grey-200'>
          <div className='w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center text-4xl'>
            {staticCategory?.emoji || '📁'}
          </div>
          <div className='flex-1'>
            <h3 className='text-xl font-semibold text-grey-900'>
              {staticCategory?.label || entity.name}
            </h3>
            <div className='text-sm text-grey-500'>{entity.name}</div>
            <div className='mt-2 flex items-center space-x-2'>
              {entity.enabled ? (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                  Enabled
                </span>
              ) : (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                  Disabled
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }}
    renderDetails={entity => (
      <div>
        <dt className='text-xs text-grey-500'>Businesses Using</dt>
        <dd className='text-sm text-grey-900'>
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-700'>
            {entity.businessCount ?? 0}
          </span>
        </dd>
      </div>
    )}
    renderForm={props => (
      <CategoryForm
        category={props.entity}
        onSubmit={props.onSubmit}
        onCancel={props.onCancel}
        loading={props.loading}
      />
    )}
    getId={entity => entity.id!}
    getCreatedAt={entity => entity.createdAt}
    getUpdatedAt={entity => entity.updatedAt}
  />
);
